/**
 * Entity history API (via pro-api BFF).
 */

import { authenticatedProApiFetch } from "./pro-api.client";
import type { ListParams, ListResult } from "../../../ui/api/listApi.types";

export type HistoryEventType = "audit" | "security" | "error" | "notification";

export type HistoryEventVisibility = "public" | "self" | "org" | "superAdmin";

export type HistoryServiceId = "user-service" | "product-service";

/** Origin service of a unified-feed row (set by the pro-api BFF). */
export type HistorySource = "user-service" | "product-service";

export interface HistoryUserRef {
  id?: string;
  displayName: string;
  email?: string;
}

export interface HistoryEvent {
  id: string;
  occurredAt: string;
  type: HistoryEventType | string;
  domain: string;
  users: HistoryUserRef[];
  event: string;
  meta: Record<string, unknown>;
  summary: string;
  visibility: HistoryEventVisibility | string;
  organizationId?: string | null;
  resourceType?: string | null;
  resourceId?: string | null;
  createdAt: string;
  /** Origin service, present on unified-feed rows. */
  source?: HistorySource;
}

/** A single page of the unified, cursor-paginated history feed. */
export interface HistoryFeedPage {
  data: HistoryEvent[];
  nextCursor: string | null;
  hasMore: boolean;
  /** Sources that failed to load for this page (feed still returns partial data). */
  degraded?: HistorySource[];
}

function buildHistoryListQuery(params: ListParams): string {
  const qs = new URLSearchParams();
  qs.set("page", String(params.page));
  qs.set("limit", String(params.limit));
  if (params.search) qs.set("search", params.search);
  if (params.sort_by) qs.set("sort_by", params.sort_by);
  if (params.order) qs.set("order", params.order);
  if (params.filters && Object.keys(params.filters).length > 0) {
    qs.set("filters", JSON.stringify(params.filters));
  }
  return qs.toString();
}

function normalizeHistoryEvent(raw: Record<string, unknown>): HistoryEvent {
  const usersRaw = raw.users;
  const users = Array.isArray(usersRaw)
    ? usersRaw.map((entry) => {
        const row = entry as Record<string, unknown>;
        return {
          ...(row.id != null ? { id: String(row.id) } : {}),
          displayName: String(row.displayName ?? row.display_name ?? "Unknown"),
          ...(row.email != null ? { email: String(row.email) } : {}),
        };
      })
    : [];

  const occurredAt =
    raw.occurredAt != null
      ? String(raw.occurredAt)
      : raw.occurred_at != null
        ? String(raw.occurred_at)
        : "";
  const createdAt =
    raw.createdAt != null
      ? String(raw.createdAt)
      : raw.created_at != null
        ? String(raw.created_at)
        : occurredAt;

  return {
    id: String(raw.id ?? ""),
    occurredAt,
    type: String(raw.type ?? "audit"),
    domain: String(raw.domain ?? ""),
    users,
    event: String(raw.event ?? ""),
    meta:
      raw.meta && typeof raw.meta === "object"
        ? (raw.meta as Record<string, unknown>)
        : {},
    summary: String(raw.summary ?? ""),
    visibility: String(raw.visibility ?? "superAdmin"),
    organizationId:
      raw.organizationId != null
        ? String(raw.organizationId)
        : raw.organization_id != null
          ? String(raw.organization_id)
          : null,
    resourceType:
      raw.resourceType != null
        ? String(raw.resourceType)
        : raw.resource_type != null
          ? String(raw.resource_type)
          : null,
    resourceId:
      raw.resourceId != null
        ? String(raw.resourceId)
        : raw.resource_id != null
          ? String(raw.resource_id)
          : null,
    createdAt,
    ...(raw.source === "user-service" || raw.source === "product-service"
      ? { source: raw.source as HistorySource }
      : {}),
  };
}

export async function fetchHistoryList(
  accessToken: string,
  service: HistoryServiceId,
  params: ListParams,
): Promise<ListResult<HistoryEvent>> {
  const query = buildHistoryListQuery(params);
  const data = await authenticatedProApiFetch<{
    data?: Record<string, unknown>[];
    total?: number;
  }>(`/history/${service}?${query}`, accessToken, { method: "GET" });
  const rows = data.data ?? [];
  return {
    data: rows.map(normalizeHistoryEvent),
    total: Number(data.total ?? rows.length),
  };
}

export interface HistoryFeedParams {
  limit: number;
  cursor?: string | null;
  search?: string;
  order?: "asc" | "desc";
  filters?: Record<string, unknown>;
}

function buildHistoryFeedQuery(params: HistoryFeedParams): string {
  const qs = new URLSearchParams();
  qs.set("limit", String(params.limit));
  if (params.cursor) qs.set("cursor", params.cursor);
  if (params.search) qs.set("search", params.search);
  if (params.order) qs.set("order", params.order);
  if (params.filters && Object.keys(params.filters).length > 0) {
    qs.set("filters", JSON.stringify(params.filters));
  }
  return qs.toString();
}

/**
 * Fetch one page of the unified, cursor-paginated history feed (merged across
 * all services by the pro-api BFF).
 */
export async function fetchUnifiedHistoryFeed(
  accessToken: string,
  params: HistoryFeedParams,
): Promise<HistoryFeedPage> {
  const query = buildHistoryFeedQuery(params);
  const data = await authenticatedProApiFetch<{
    data?: Record<string, unknown>[];
    nextCursor?: string | null;
    hasMore?: boolean;
    degraded?: HistorySource[];
  }>(`/history?${query}`, accessToken, { method: "GET" });
  const rows = data.data ?? [];
  return {
    data: rows.map(normalizeHistoryEvent),
    nextCursor: data.nextCursor ?? null,
    hasMore: Boolean(data.hasMore),
    ...(Array.isArray(data.degraded) && data.degraded.length > 0
      ? { degraded: data.degraded }
      : {}),
  };
}
